import { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { hexToHsl } from "@/lib/color";
import { useAuth } from "@/context/AuthContext";

const ShopContext = createContext(null);

function applyTheme(primaryHex, accentHex) {
  const root = document.documentElement;
  const p = hexToHsl(primaryHex);
  const a = hexToHsl(accentHex);
  if (p) {
    root.style.setProperty("--primary", p);
    root.style.setProperty("--brand", p);
    root.style.setProperty("--ring", p);
  }
  if (a) {
    root.style.setProperty("--accent", a);
    root.style.setProperty("--brand-secondary", a);
  }
}

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const shopQuery = useQuery({ queryKey: ["shop"], queryFn: api.getShop, staleTime: 60_000 });
  const themeQuery = useQuery({ queryKey: ["theme"], queryFn: api.getTheme, staleTime: 60_000 });

  // A signed-in customer's personal skin overrides the shop default; otherwise use shop theme.
  useEffect(() => {
    const primary = user?.personal_theme_primary || themeQuery.data?.primary;
    const accent = user?.personal_theme_accent || themeQuery.data?.accent;
    if (primary) applyTheme(primary, accent);
  }, [themeQuery.data, user?.personal_theme_primary, user?.personal_theme_accent]);

  return (
    <ShopContext.Provider value={{ shop: shopQuery.data, shopLoading: shopQuery.isLoading, theme: themeQuery.data }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used inside ShopProvider");
  return ctx;
}
